import React, { useState } from "react";
import { formatDateDDMMYY } from "../../utils";
import AparFullDetailModal from "../../container/apar/AparFullDetailModal";
import { ManagerInfo } from "./aparManagerUtils";

// Re-exported so existing imports from this file keep working.
export { normalizeManagers, mergeManagerData } from "./aparManagerUtils";
export type { ManagerInfo } from "./aparManagerUtils";

interface AparEmployeeHeaderProps {
  employeeName?: string;
  employeeNumber?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  reportingManager?: ManagerInfo | null;
  reviewingManager?: ManagerInfo | null;
  acceptingAuthority?: ManagerInfo | null;
  employeeCode?: string;
  // When both are set, a "View Details" button opens the full appraisal details modal.
  detailViewerRole?: "reporting" | "reviewing" | "accepting";
  kpiReportId?: any;
}

const AparEmployeeHeader: React.FC<AparEmployeeHeaderProps> = ({
  employeeName,
  employeeNumber,
  effectiveFrom,
  effectiveTo,
  reportingManager,
  reviewingManager,
  acceptingAuthority,
  employeeCode,
  detailViewerRole,
  kpiReportId,
}) => {
  const [showDetailModal, setShowDetailModal] = useState(false);

  if (!employeeName) return null;

  const canViewDetails = !!detailViewerRole && !!kpiReportId;

  const managerFields = [
    { label: "RO1 (Reporting Manager)", info: reportingManager },
    { label: "RO2 (Reviewing Manager)", info: reviewingManager },
    { label: "Acceptance Manager", info: acceptingAuthority },
  ].filter((m) => m.info && (m.info.name || m.info.employeeNumber));

  return (
    <div className="apar-employee-header bg-light border rounded px-3 py-2 mb-3 d-flex align-items-center justify-content-between" style={{ gap: "1rem" }}>
      <div className="d-flex flex-wrap" style={{ columnGap: "2.5rem", rowGap: "0.5rem" }}>
        <div>
          <div className="text-muted small">Employee</div>
          <div><strong>{employeeName}</strong></div>
        </div>

        {employeeNumber && (
          <div>
            <div className="text-muted small">Employee Code</div>
            <div><strong>{employeeNumber}</strong></div>
          </div>
        )}

        {managerFields.map((m) => (
          <div key={m.label}>
            <div className="text-muted small">{m.label}</div>
            <div>
              <strong>{m.info?.name || "—"}</strong>
              {m.info?.employeeNumber && <span className="text-muted"> ({m.info.employeeNumber})</span>}
            </div>
          </div>
        ))}

        {(effectiveFrom || effectiveTo) && (
          <div>
            <div className="text-muted small">APAR Period</div>
            <div><strong>{effectiveFrom ? formatDateDDMMYY(effectiveFrom) : "—"} to {effectiveTo ? formatDateDDMMYY(effectiveTo) : "Present"}</strong></div>
          </div>
        )}
      </div>

      {canViewDetails && (
        <>
          <button
            type="button"
            className="btn btn-sm apar-view-details-btn text-nowrap"
            onClick={() => setShowDetailModal(true)}
            title="View all appraisal details filled so far"
          >
            <i className="fa fa-file-text-o mr-1" />View Details
          </button>
          <AparFullDetailModal
            isOpen={showDetailModal}
            toggle={() => setShowDetailModal(false)}
            viewerRole={detailViewerRole}
            employeeData={{ employeeCode, employeeName, employeeNumber, effectiveFrom, effectiveTo, reportingManager, reviewingManager, acceptingAuthority }}
            kpiReportId={kpiReportId}
          />
        </>
      )}
    </div>
  );
};

export default AparEmployeeHeader;
